import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Search, ChevronDown, ChevronUp, X, Check } from 'lucide-react';

const DELIVERY_TYPES = ['DOM','SONA','GRAB','JT_COD','TAKE_SHOP'];
const SHIPMENT_STATUSES = ['sent','returned','change_location','cancelled','sent_returned','non_cod'];
const STATUS_COLORS = { sent:'#16a34a', returned:'#dc2626', change_location:'#f59e0b', cancelled:'#6b7280', sent_returned:'#0369a1', non_cod:'#7c3aed' };
const STATUS_LABELS = { sent:'Sent', returned:'Returned', change_location:'Change Location', cancelled:'Cancelled', sent_returned:'Sent+Returned', non_cod:'Non COD' };
const PAY_METHODS = ['cash','aba_123','aba_d','non_cod','split_cash_aba'];
const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 9px', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' };

function BillSearch({ onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(()=>{ const h=e=>{ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h); },[]);

  const search = async (term) => {
    if (!term) return setResults([]);
    try {
      const r = await api.get('/billing/bills', { params:{search:term,per_page:10,status:'packed'} });
      setResults(r.data.bills||[]);
    } catch {}
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <div style={{ position:'relative' }}>
        <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
        <input value={q} onChange={e=>{ setQ(e.target.value); setOpen(true); setTimeout(()=>search(e.target.value),300); }}
          placeholder="Search bill number or customer..." style={{ ...inp, paddingLeft:28 }} />
      </div>
      {open&&results.length>0&&(
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:300, background:'#fff', border:'1px solid #e5e7eb', borderRadius:6, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', maxHeight:220, overflowY:'auto', marginTop:2 }}>
          {results.map(b=>(
            <div key={b.id} onMouseDown={()=>{ onSelect(b); setQ(''); setResults([]); setOpen(false); }}
              style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #f3f4f6', fontSize:13 }}
              onMouseEnter={e=>e.currentTarget.style.background='#f0f9ff'}
              onMouseLeave={e=>e.currentTarget.style.background=''}>
              <span style={{ fontFamily:'monospace', color:'#7c6af5', fontWeight:700, marginRight:10 }}>{b.bill_number}</span>
              <span style={{ color:'#374151' }}>{b.customer_name}</span>
              <span style={{ color:'#16a34a', marginLeft:8, fontWeight:600 }}>${parseFloat(b.total||0).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddShipmentRow({ sessionId, onAdded }) {
  const [bill, setBill] = useState(null);
  const [form, setForm] = useState({ status:'sent', payment_method:'cash', amount_usd:'', amount_khr:'', aba_transaction_id:'', tips_usd:'', notes:'' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!bill) return toast.error('Select a bill');
    if (!form.status) return toast.error('Select status');
    setSaving(true);
    try {
      await api.post(`/billing/sessions/${sessionId}/shipments`, { bill_id:bill.id, ...form, amount_usd:parseFloat(form.amount_usd||0), amount_khr:parseFloat(form.amount_khr||0), tips_usd:parseFloat(form.tips_usd||0) });
      toast.success('Recorded');
      setBill(null); setForm({ status:'sent', payment_method:'cash', amount_usd:'', amount_khr:'', aba_transaction_id:'', tips_usd:'', notes:'' });
      onAdded();
    } catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ background:'#f0f9ff', border:'1px dashed #bae6fd', borderRadius:8, padding:14, marginBottom:10 }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#0369a1', marginBottom:10 }}>+ Add Record</div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr', gap:8, alignItems:'end' }}>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Bill</div>
          {bill
            ? <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <span style={{ fontFamily:'monospace', color:'#7c6af5', fontWeight:700, fontSize:12 }}>{bill.bill_number}</span>
                <span style={{ fontSize:12, color:'#374151' }}>{bill.customer_name}</span>
                <button onClick={()=>setBill(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><X size={12}/></button>
              </div>
            : <BillSearch onSelect={setBill} />}
        </div>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Status</div>
          <select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} style={inp}>
            {SHIPMENT_STATUSES.map(s=><option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Payment</div>
          <select value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))} style={inp}>
            {PAY_METHODS.map(m=><option key={m} value={m}>{m.replace('_',' ')}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>Amount USD</div>
          <input type="number" step="0.01" value={form.amount_usd} onChange={e=>setForm(f=>({...f,amount_usd:e.target.value}))} style={inp} placeholder="$0.00" />
        </div>
        <div>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>ABA Ref</div>
          <input value={form.aba_transaction_id} onChange={e=>setForm(f=>({...f,aba_transaction_id:e.target.value}))} style={inp} placeholder="178..." />
        </div>
      </div>
      <div style={{ display:'flex', gap:8, marginTop:8, alignItems:'center' }}>
        <div style={{ flex:1 }}>
          <input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={inp} placeholder="Notes..." />
        </div>
        <button onClick={save} disabled={saving} style={{ background:'#0369a1', color:'#fff', border:'none', borderRadius:6, padding:'7px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          {saving?'Saving...':'Add'}
        </button>
      </div>
    </div>
  );
}

function SessionDetail({ session, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);

  const load = async () => {
    try { const r=await api.get(`/billing/sessions/${session.id}`); setDetail(r.data); } catch {}
    finally { setLoading(false); }
  };

  useEffect(()=>{ load(); },[session.id]);

  const closeSession = async () => {
    if (!window.confirm('Close this session? Totals will be calculated.')) return;
    setClosing(true);
    try { await api.post(`/billing/sessions/${session.id}/close`); toast.success('Session closed'); load(); onUpdated(); }
    catch { toast.error('Failed'); }
    finally { setClosing(false); }
  };

  if (loading) return <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ background:'#fff', borderRadius:12, padding:40, color:'#6b7280' }}>Loading...</div></div>;

  const shipments = detail?.shipments||[];
  const s = detail||session;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'flex-start', justifyContent:'flex-end' }}>
      <div style={{ background:'#fff', width:680, height:'100vh', overflowY:'auto', boxShadow:'-4px 0 30px rgba(0,0,0,0.15)' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'#111827' }}>{s.delivery_type} — {s.session_date}</div>
            <div style={{ fontSize:12, color:'#6b7280' }}>{s.delivery_person_name||'—'} · {s.status==='open'?'🟢 Open':'🔒 Closed'}</div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {s.status==='open'&&<button onClick={closeSession} disabled={closing} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', fontSize:13, fontWeight:600, cursor:'pointer' }}>{closing?'Closing...':'🔒 Close Session'}</button>}
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><X size={18}/></button>
          </div>
        </div>

        <div style={{ padding:20 }}>
          {/* Summary */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
            {[['Packages',s.total_packages,'#111827'],['Sent',s.total_sent,'#16a34a'],['Returned',s.total_returned,'#dc2626'],['Changed',s.total_change_location,'#f59e0b']].map(([label,val,color])=>(
              <div key={label} style={{ background:'#f9fafb', borderRadius:8, padding:'10px 14px', textAlign:'center' }}>
                <div style={{ fontSize:20, fontWeight:700, color }}>{val||0}</div>
                <div style={{ fontSize:11, color:'#6b7280' }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
            {[['Cash USD',`$${parseFloat(s.total_cash_usd||0).toFixed(2)}`,'#16a34a'],['ABA USD',`$${parseFloat(s.total_aba_usd||0).toFixed(2)}`,'#2563eb'],['Delivery Payout',`${parseFloat(s.delivery_payout_khr||0).toLocaleString()}៛`,'#f59e0b']].map(([label,val,color])=>(
              <div key={label} style={{ background:'#f9fafb', borderRadius:8, padding:'10px 14px', textAlign:'center' }}>
                <div style={{ fontSize:16, fontWeight:700, color }}>{val}</div>
                <div style={{ fontSize:11, color:'#6b7280' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Add record */}
          {s.status==='open'&&<AddShipmentRow sessionId={session.id} onAdded={load} />}

          {/* Records table */}
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'#f9fafb' }}>
                  {['#','Bill','Customer','$','Status','Payment','ABA Ref','Notes'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shipments.length===0&&<tr><td colSpan={8} style={{ padding:24, textAlign:'center', color:'#9ca3af' }}>No records yet</td></tr>}
                {shipments.map((sh,i)=>(
                  <tr key={sh.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ padding:'6px 10px', color:'#9ca3af', fontSize:11 }}>{i+1}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'monospace', color:'#7c6af5', fontWeight:700, fontSize:11 }}>{sh.bill_number}</td>
                    <td style={{ padding:'6px 10px', color:'#374151' }}>{sh.customer_name}<br/><span style={{ color:'#9ca3af', fontSize:10 }}>{sh.customer_phone}</span></td>
                    <td style={{ padding:'6px 10px', fontWeight:700, color:'#16a34a' }}>${parseFloat(sh.bill_total||0).toFixed(2)}</td>
                    <td style={{ padding:'6px 10px' }}><span style={{ fontSize:10, fontWeight:600, padding:'2px 6px', borderRadius:6, background:STATUS_COLORS[sh.status]+'20', color:STATUS_COLORS[sh.status] }}>{STATUS_LABELS[sh.status]||sh.status}</span></td>
                    <td style={{ padding:'6px 10px', color:'#374151' }}>{sh.payment_method||'—'}{sh.amount_usd>0&&<span style={{ color:'#16a34a', marginLeft:4 }}>${parseFloat(sh.amount_usd).toFixed(2)}</span>}</td>
                    <td style={{ padding:'6px 10px', color:'#9ca3af', fontSize:10 }}>{sh.aba_transaction_id||'—'}</td>
                    <td style={{ padding:'6px 10px', color:'#6b7280' }}>{sh.notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShipmentsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ session_date: new Date().toISOString().slice(0,10), delivery_type:'DOM', delivery_person_name:'' });
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(()=>{ load(); },[filterType, filterStatus]);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get('/billing/sessions', { params:{ delivery_type:filterType||undefined, status:filterStatus||undefined }});
      setSessions(r.data||[]);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }

  const createSession = async () => {
    try {
      const r = await api.post('/billing/sessions', newForm);
      toast.success('Session created');
      setShowNew(false);
      setSelected(r.data.session);
      load();
    } catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
  };

  const sinp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'7px 10px', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#111827' }}>🚚 Delivery Sessions</h1>
          <p style={{ margin:'2px 0 0', fontSize:13, color:'#6b7280' }}>Daily delivery tracking — replacing Google Sheets</p>
        </div>
        <button onClick={()=>setShowNew(true)} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
          <Plus size={14}/> New Session
        </button>
      </div>

      {/* Filters */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', padding:'12px 14px', marginBottom:16, display:'flex', gap:10 }}>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ ...sinp, maxWidth:150 }}>
          <option value="">All Types</option>
          {DELIVERY_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...sinp, maxWidth:130 }}>
          <option value="">All Status</option>
          <option value="open">🟢 Open</option>
          <option value="closed">🔒 Closed</option>
        </select>
      </div>

      {/* Sessions list */}
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {loading&&<div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>Loading...</div>}
        {!loading&&sessions.length===0&&<div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>No sessions yet</div>}
        {sessions.map(s=>(
          <div key={s.id} onClick={()=>setSelected(s)}
            style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'14px 18px', cursor:'pointer', display:'flex', alignItems:'center', gap:16 }}
            onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
            onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                <span style={{ fontWeight:700, fontSize:14, color:'#111827' }}>{s.delivery_type}</span>
                <span style={{ fontSize:12, color:'#6b7280' }}>{s.session_date}</span>
                <span style={{ fontSize:11, background:s.status==='open'?'#dcfce7':'#f3f4f6', color:s.status==='open'?'#16a34a':'#6b7280', padding:'1px 8px', borderRadius:10, fontWeight:600 }}>{s.status==='open'?'🟢 Open':'🔒 Closed'}</span>
              </div>
              <div style={{ display:'flex', gap:16, fontSize:12, color:'#6b7280' }}>
                <span>👤 {s.delivery_person_name||'—'}</span>
                <span>📦 {s.total_packages} packages</span>
                <span style={{ color:'#16a34a' }}>✅ {s.total_sent} sent</span>
                <span style={{ color:'#dc2626' }}>↩️ {s.total_returned} returned</span>
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#16a34a' }}>${parseFloat(s.total_cash_usd||0).toFixed(2)}</div>
              <div style={{ fontSize:11, color:'#9ca3af' }}>cash collected</div>
              {s.delivery_payout_khr>0&&<div style={{ fontSize:11, color:'#f59e0b' }}>{parseFloat(s.delivery_payout_khr).toLocaleString()}៛ payout</div>}
            </div>
          </div>
        ))}
      </div>

      {/* New session modal */}
      {showNew&&(
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, width:360, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:16, color:'#111827' }}>New Delivery Session</div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 }}>Date</label>
              <input type="date" value={newForm.session_date} onChange={e=>setNewForm(f=>({...f,session_date:e.target.value}))} style={sinp} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 }}>Delivery Type</label>
              <select value={newForm.delivery_type} onChange={e=>setNewForm(f=>({...f,delivery_type:e.target.value}))} style={sinp}>
                {DELIVERY_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 }}>Delivery Person</label>
              <input value={newForm.delivery_person_name} onChange={e=>setNewForm(f=>({...f,delivery_person_name:e.target.value}))} style={sinp} placeholder="e.g. DOM, Seng, Piseth..." />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setShowNew(false)} style={{ flex:1, background:'#f3f4f6', color:'#374151', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={createSession} style={{ flex:1, background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {selected&&<SessionDetail session={selected} onClose={()=>setSelected(null)} onUpdated={load} />}
    </div>
  );
}
